/**
 * @module host
 */

import hoistStatics from 'hoist-non-react-statics';
import React from 'react';
import {throwError, AppActionType} from '../actions/Error';
import {appBridgeMiddlewareProptype, ApiClientConfig, Application, Middleware} from './';

export type ReactComponent<P> = React.StatelessComponent<P> | React.ComponentClass<P>;

export interface Context {
  appBridgeMiddleware: Middleware;
}

export interface DispatchToProps {
  (app: Application): any;
}

export interface WithAppProps<Store> {
  app: Application;
  store: Store;
}

export interface RequiredProps {
  config: ApiClientConfig;
}

export interface State {
  mounted: Boolean;
}

function getComponentName(component: any) {
  return (component && (component.displayName || component.name)) || 'Component';
}

export type StoreToProps<Store> = (obj: any) => Store;

export function withApp<Store>(
  storeToProps?: StoreToProps<Store>,
  dispatchToProps?: DispatchToProps,
) {
  return function<OwnProps>(
    WrappedComponent: ReactComponent<OwnProps>,
  ): React.ComponentClass<OwnProps & WithAppProps<Store>> {
    class AppBridgeConnector extends React.Component<any, State> {
      static contextTypes = {
        appBridgeMiddleware: appBridgeMiddlewareProptype,
      };
      static displayName = `AppBridge(${getComponentName(WrappedComponent)})`;
      static WrappedComponent = WrappedComponent;

      app!: Application;
      context!: Context;
      mappedProps?: any;
      state: State = {
        mounted: false,
      };

      constructor(props: OwnProps & RequiredProps, context: Context) {
        super(props, context);
      }

      augmentProps() {
        const {props, app, mappedProps} = this;
        const appState = app.getState();
        const store = storeToProps ? storeToProps(appState) : appState;
        const augmented = {...props, app, store};
        if (mappedProps) {
          return {
            ...mappedProps,
            ...augmented,
          };
        }

        return augmented;
      }

      componentDidMount() {
        const {config} = this.props;
        const {appBridgeMiddleware} = this.context;

        if (!config) {
          throwError(AppActionType.MISSING_CONFIG, 'Missing required prop `config`');
        }

        if (!appBridgeMiddleware || typeof appBridgeMiddleware.load !== 'function') {
          throwError(
            AppActionType.MISSING_APP_BRIDGE_MIDDLEWARE,
            'Missing required context `appBridgeMiddleware`. Maybe you forgot the App Bridge `<Provider>` component?',
          );
        }

        this.app = this.context.appBridgeMiddleware.load({
          config,
          type: 'application',
        });

        if (dispatchToProps) {
          this.mappedProps = dispatchToProps(this.app);
        }
        this.setState({mounted: true});
      }

      render() {
        return this.state.mounted ? <WrappedComponent {...this.augmentProps()} /> : null;
      }
    }

    const FinalComponent = hoistStatics(AppBridgeConnector, WrappedComponent);

    return FinalComponent as React.ComponentClass<OwnProps & WithAppProps<Store>>;
  };
}



// WEBPACK FOOTER //
// ./src/host/decorator.tsx